import './scanner.css'

import React, { useEffect, useRef, useState } from 'react'
import * as ZXingBrowser from '@zxing/browser'
import { BinaryBitmap, HybridBinarizer, RGBLuminanceSource } from '@zxing/library'
import { Services } from '@/services/services'
import { LoggedInPageLayout } from '../layout/LoggedInLayout'
import { BackButton } from '../layout/NavBar'
import { isValidIMEI } from '@/common/validators'
import { CurrentSession } from '@/services/repository/session'

interface QRScannerProps {
  services: Services
  onScan: (data: string) => void
}

export const QRScanner: React.FC<QRScannerProps> = ({ onScan }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [permissionDenied, setPermissionDenied] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const isScanningRef = useRef<boolean>(true)

  const permErrorMsg = 'Camera access denied. Please allow camera access in your browser settings.'

  useEffect(() => {
    isScanningRef.current = true
    let animationFrameId: number

    const startScan = async () => {
      try {
          setIsLoading(true)
          setCameraError(null)
          setPermissionDenied(false)

          // 1. Define ideal video constraints prioritizing the back camera
          const constraints: MediaStreamConstraints = {
            video: {
              // 'environment' tells the OS to specifically use the rear-facing camera array
              facingMode: { ideal: 'environment' },
              // Optional: request a good scanning resolution
              width: { ideal: 1280 },
              height: { ideal: 720 },
              frameRate: { ideal: 30 }
            }
          }

          // 2. Request the stream directly using these constraints
          // This prompts iOS for permission and naturally selects the default back camera
          const stream = await navigator.mediaDevices.getUserMedia(constraints)
          
          streamRef.current = stream
          if (videoRef.current) {
            videoRef.current.srcObject = stream
            videoRef.current.play()
          }

          // 3. Now that permissions are granted, if you absolutely need to know 
          // which specific device ID was picked by the system, you can extract it:
          const videoTrack = stream.getVideoTracks()[0]
          const settings = videoTrack ? videoTrack.getSettings() : null
          const activeDeviceId = settings?.deviceId

          // Proceed with your ZXing canvas scanning loop...
          const codeReader = new ZXingBrowser.BrowserQRCodeReader()
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d', { willReadFrequently: true })
          
          setIsLoading(false)

        // Processing loop
        const processFrame = async () => {
          if (!isScanningRef.current || !videoRef.current || !ctx) return

          if (videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
            const width = videoRef.current.videoWidth
            const height = videoRef.current.videoHeight
            canvas.width = width
            canvas.height = height

            // Draw current video frame to canvas
            ctx.drawImage(videoRef.current, 0, 0, width, height)
            
            try {
              // 1. Try reading normally
              const result = await codeReader.decodeFromCanvas(canvas)
              onScan(result.getText())
            } catch (normalError) {
              // 2. Normal failed, try processing inverted frame
              try {
                const imgData = ctx.getImageData(0, 0, width, height)
                const data = imgData.data
                
                // Invert colors (RGB only, leave Alpha untouched)
                for (let i = 0; i < data.length; i += 4) {
                  data[i] = 255 - data[i]       // R
                  data[i + 1] = 255 - data[i + 1] // G
                  data[i + 2] = 255 - data[i + 2] // B
                }
                ctx.putImageData(imgData, 0, 0)

                // Try reading inverted canvas
                const resultInverted = await codeReader.decodeFromCanvas(canvas)
                onScan(resultInverted.getText())
              } catch (invertedError) {
                // Both attempts failed for this frame; continue loop silently
              }
            }
          }

          // Throttle scanning slightly to prevent CPU choking (~100ms delay)
          setTimeout(() => {
            if (isScanningRef.current) {
              animationFrameId = requestAnimationFrame(processFrame)
            }
          }, 100)
        }

        animationFrameId = requestAnimationFrame(processFrame)

      } catch (error: any) {
        setIsLoading(false)
        if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
          setPermissionDenied(true)
        } else {
          setCameraError('Error initializing camera: ' + error?.message)
        }
      }
    }

    startScan()

    return () => {
      isScanningRef.current = false
      cancelAnimationFrame(animationFrameId)
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop())
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null
      }
    }
  }, [onScan])

  return (
    <div className='qr-scanner'>
      {isLoading && <div className='spinner'></div>}
      {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
      {cameraError && <div className="camera-error">{cameraError}</div>}
      <video
        ref={videoRef}
        playsInline
        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        muted
      />
    </div>
  )
}

export const QRScannerPage: React.FC<{ session: CurrentSession, services: Services }> = ({ services, session }) => {
  const [scannerData, setScannerData] = useState<string>()

  return (
    <LoggedInPageLayout pageName='qr-scanner' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <div>
        <h1>QR Code Scanner</h1>
        <QRScanner
          onScan={(data: string) => setScannerData(data)}
          services={services}
        />
        {scannerData
          ? isValidIMEI(scannerData)
            ? scannerData
            : `${scannerData} is not correct imei`
          : null}
      </div>
    </LoggedInPageLayout>
  )
}


// import './scanner.css'

// import React, { useEffect, useRef, useState } from 'react'
// import { BrowserMultiFormatReader, BrowserQRCodeReader } from '@zxing/browser'
// import * as ZXingBrowser from '@zxing/browser'
// import { DecodeHintType } from '@zxing/library'
// import { CurrentSession } from '@/services/repository/session'
// import { Services } from '@/services/services'
// import { LoggedInPageLayout } from '../layout/LoggedInLayout'
// import { isValidIMEI } from '@/common/validators'
// import { BackButton } from '../layout/NavBar'

// interface QRScannerProps {
//   services: Services
//   onScan: (data: string) => void
// }

// interface SystemVideoAPIRef {
//   stream?: MediaStream
//   controls: { 
//     stop: () => void 
//   }
// }

// export const QRScanner: React.FC<QRScannerProps> = ({ onScan }) => {
//   const [isLoading, setIsLoading] = useState(false)
//   const [permissionDenied, setPermissionDenied] = useState(false)
//   const videoRef = useRef<HTMLVideoElement>(null)
//   const systemRef = useRef<SystemVideoAPIRef | null>(null)
//   const [cameraError, setCameraError] = useState<string | null>(null)
//   const permErrorMsg = 'Camera access denied. Please allow camera access in your browser settings.' 

//   useEffect(() => {
//     let cancelled = false

//     const startScan = async () => {
//       try {
//         setIsLoading(true)
//         setCameraError(null)
//         setPermissionDenied(false)


//         const hints = new Map()
//         hints.set(DecodeHintType.TRY_HARDER, true)
//         // hints.set(DecodeHintType.ALSO_INVERTED, true) <- Property 'ALSO_INVERTED' does not exist on type 'typeof DecodeHintType'.


//         const codeReader = new ZXingBrowser.BrowserMultiFormatReader(hints)
//         const videoInputDevices = await ZXingBrowser.BrowserCodeReader.listVideoInputDevices()

//         // Try to select a back camera
//         let selectedDevice = videoInputDevices.find(
//           (d) => d.label && d.label.toLowerCase().includes('back')
//         )
//         // Fallback: pick the second device (often back on iOS!)
//         if (!selectedDevice && videoInputDevices.length > 1) {
//           selectedDevice = videoInputDevices[1]
//         }
//         // Otherwise, use first device
//         if (!selectedDevice && videoInputDevices.length > 0) {
//           selectedDevice = videoInputDevices[0]
//         }

//         if (!selectedDevice || !videoRef.current) throw new Error('No camera device found')

//         const controls = await codeReader.decodeFromVideoDevice(
//           selectedDevice.deviceId,
//           videoRef.current,
//           (result: any, err: any, controls: any) => {
//             if (cancelled) {
//               controls.stop()
//               return
//             }
//             setIsLoading(false)
//             if (result) {
//               // controls.stop() // Uncomment to stop after first successful scan
//               onScan(result.getText())
//             }
//           }
//         )
//         systemRef.current = {
//           // stream,
//           controls
//         }
//       } catch (error) {
//         if (
//           typeof error === 'object' &&
//           error !== null &&
//           'name' in error &&
//           (
//             (error as any).name === 'NotAllowedError' ||
//             (error as any).name === 'PermissionDeniedError'
//           )
//         ) {
//           setPermissionDenied(true)
//         } else if (
//           typeof error === 'object' &&
//           error !== null &&
//           'name' in error &&
//           (error as any).name === 'NotReadableError'
//         ) {
//           setCameraError(
//             'Could not access the camera. On Android: ensure no other app or tab is using the camera, try using Google Chrome, grant camera permission, and try again after tapping "Start QR Scanner".'
//           )
//         } else {
//           setCameraError('Unknown error initializing camera: ' + (error as any)?.message)
//         }
//         setIsLoading(false)
//       }
//     }

//     startScan()
    
//     return () => {
//       cancelled = true
//       if(systemRef.current) {
//         const stream = systemRef.current.stream
//         const constrols = systemRef.current.controls
//         if(constrols) {
//           constrols.stop()
//         }
//         if(stream) {
//           stream.getTracks().forEach((track) => track.stop())
//         }
//         systemRef.current = null
//       }
//       if (videoRef.current) {
//         videoRef.current.srcObject = null
//       }
//     }
//   }, [onScan])

//   // UI: Only start scanner after click
//   // if (!started)
//   //   return (
//   //     <div className="qr-scanner">
//   //       <button onClick={() => setStarted(true)}>Start QR Scanner</button>
//   //       {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
//   //       {cameraError && <div className="camera-error">{cameraError}</div>}
//   //     </div>
//   //   )

//   return (
//     <div className='qr-scanner'>
//       {isLoading ? <div className='spinner'></div> : null}
//       {permissionDenied && <div className='camera-error'>{permErrorMsg}</div>}
//       {cameraError && <div className="camera-error">{cameraError}</div>}
//       <video
//         ref={videoRef}
//         playsInline
//         style={{ width: '100%', height: '100%', objectFit: 'cover' }}
//         muted
//       />
//     </div>
//   )
// }

// export const QRScannerPage: React.FC<{ session: CurrentSession, services: Services }> = ({ services, session }) => {
//   const [scannerData, setScannerData] = useState<string>()

//   return (
//     <LoggedInPageLayout pageName='qr-scanner' services={services} session={session}
//       navbarLeft={<BackButton services={services} translates={session.translates} />}>
//       <div>
//         <h1>QR Code Scanner</h1>
//         <QRScanner
//           onScan={(data: string) => setScannerData(data)}
//           services={services}
//         />
//         {scannerData
//           ? isValidIMEI(scannerData)
//             ? scannerData
//             : `${scannerData} is not correct imei`
//           : null}
//       </div>
//     </LoggedInPageLayout>
//   )
// }